import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";
import { io, Socket } from "socket.io-client";
import { useMessageInsightStore } from "./useMessageInsightStore";
import type { MessageInsight } from "../types/messageInsight";
import { isOmniHost, OMNI_ROOT } from "../lib/tenant";
import { API_ROOT } from "../lib/api";

// Socket host follows the same env-driven API root as REST calls, so a staging
// build's socket can never point at the live backend (and vice-versa).
const BASE_URL = API_ROOT;

export interface AuthUser {
  _id: string;
  email: string;
  fullName: string;
  username?: string;
  profilePic?: string;
  role: "Admin" | "Supervisor" | "Sales Director" | "Business Developer" | "Client" | "Finance User";
  commissionPercent?: number;
  slackWebhookUrl?: string;
  reportRecipientEmails?: string[];
  reportingManager?: string | null;
  mustChangePassword?: boolean;
  // Client users: the Brand/Tenant they belong to (server-derived).
  tenant?: { name: string; slug: string; subdomain: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface AuthCredentials {
  email: string;
  password: string;
  name?: string;
  profilePic?: string;
}

interface AuthState {
  authUser: AuthUser | null;
  isSigningUp: boolean;
  isLoggingIn: boolean;
  isUpdatingProfile: boolean;
  isCheckingAuth: boolean;
  onlineUsers: string[];
  socket: Socket | null;

  checkAuth: () => Promise<void>;
  signup: (data: AuthCredentials) => Promise<void>;
  login: (data: AuthCredentials) => Promise<{ twoFactorRequired?: boolean; userId?: string } | null>;
  verifyTwoFactor: (data: { userId: string; code: string }) => Promise<boolean>;
  changePassword: (data: { currentPassword?: string; newPassword: string }) => Promise<boolean>;
  logout: () => Promise<void>;
  updateProfile: (data: Partial<AuthUser>) => Promise<void>;
  connectSocket: () => void;
  disconnectSocket: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  authUser: null,
  isSigningUp: false,
  isLoggingIn: false,
  isUpdatingProfile: false,
  isCheckingAuth: true,
  onlineUsers: [],
  socket: null,

  checkAuth: async () => {
    try {
      const res = await axiosInstance.get<AuthUser>("/auth/check");
      set({ authUser: res.data });
      get().connectSocket();
    } catch (error: any) {
      console.error("Error in checkAuth:", error);
      set({ authUser: null });
    } finally {
      set({ isCheckingAuth: false });
    }
  },

  signup: async (data) => {
    set({ isSigningUp: true });
    try {
      const res = await axiosInstance.post<AuthUser>("/auth/signup", data);
      set({ authUser: res.data });
      toast.success("Account created successfully");
      get().connectSocket();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Signup failed");
    } finally {
      set({ isSigningUp: false });
    }
  },

  login: async (data) => {
    set({ isLoggingIn: true });
    try {
      const res = await axiosInstance.post<any>("/auth/login", data);
      // Two-factor accounts return a challenge instead of a session.
      if (res.data?.twoFactorRequired) {
        toast.success("Enter the code we emailed you");
        return { twoFactorRequired: true, userId: res.data.userId };
      }
      // Central omni login → hand over to the client's branded environment
      // (the session cookie is scoped to the apex domain, so it carries over).
      if (res.data?.tenantRedirect) {
        window.location.href = res.data.tenantRedirect;
        return null;
      }
      set({ authUser: res.data });
      toast.success("Logged in successfully");
      get().connectSocket();
      return null;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Login failed");
      return null;
    } finally {
      set({ isLoggingIn: false });
    }
  },

  verifyTwoFactor: async (data) => {
    set({ isLoggingIn: true });
    try {
      const res = await axiosInstance.post<AuthUser>("/auth/verify-2fa", data);
      if ((res.data as any)?.tenantRedirect) {
        window.location.href = (res.data as any).tenantRedirect;
        return true;
      }
      set({ authUser: res.data });
      toast.success("Logged in successfully");
      get().connectSocket();
      return true;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Verification failed");
      return false;
    } finally {
      set({ isLoggingIn: false });
    }
  },

  changePassword: async (data) => {
    try {
      await axiosInstance.put("/auth/change-password", data);
      // Reflect the cleared temp-password flag locally.
      set((s) => (s.authUser ? { authUser: { ...s.authUser, mustChangePassword: false } } : {}));
      toast.success("Password updated");
      return true;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Could not update password");
      return false;
    }
  },

  logout: async () => {
    try {
      await axiosInstance.post("/auth/logout");
      set({ authUser: null });
      toast.success("Logged out successfully");
      get().disconnectSocket();
      // Omni tenant environments return to the neutral central entry point.
      if (isOmniHost()) window.location.href = `https://${OMNI_ROOT}`;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Logout failed");
    }
  },

  updateProfile: async (data) => {
    set({ isUpdatingProfile: true });
    try {
      const res = await axiosInstance.put<AuthUser>("/auth/update-profile", data);
      set({ authUser: res.data });
      toast.success("Profile updated successfully");
    } catch (error: any) {
      console.error("Error in update profile:", error);
      toast.error(error.response?.data?.message || "Update failed");
    } finally {
      set({ isUpdatingProfile: false });
    }
  },

 connectSocket: () => {
  const { authUser, socket } = get();
  if (!authUser || socket?.connected) return;

  const newSocket = io(BASE_URL, {
    query: { userId: authUser._id },
  });

  set({ socket: newSocket });

  newSocket.on("getOnlineUsers", (userIds: string[]) => {
    set({ onlineUsers: userIds });
  });

newSocket.on("messageInsight", (insight: MessageInsight) => {

  useMessageInsightStore.getState().addInsight(insight);
});

// ERP → CRE live sync: a project/milestone changed in ERP. Broadcast a window
// event so open views (Projects list, Inbox, milestone modal) refetch without
// a manual refresh.
newSocket.on("projectSync", (data: any) => {
  window.dispatchEvent(new CustomEvent("cre:project-sync", { detail: data }));
});

// Live invoice payment updates (webhook-confirmed) → refresh open invoice lists.
newSocket.on("invoiceSync", (data: any) => {
  window.dispatchEvent(new CustomEvent("cre:invoice-sync", { detail: data }));
});

// Generic in-app notifications (e.g. a BD's proposal was viewed by a client).
newSocket.on("notification", (n: { type?: string; title?: string; body?: string }) => {
  if (n?.title || n?.body) {
    toast(`${n.title ? n.title + " — " : ""}${n.body || ""}`, { icon: "🔔" });
  }
  window.dispatchEvent(new CustomEvent("cre:notification", { detail: n }));
});

},

  disconnectSocket: () => {
    const { socket } = get();
    if (socket?.connected) socket.disconnect();
  },
}));

